// src/services/chunkUploader.js

/**
 * Chunked uploader for backend-mediated uploads:
 *  - POST   /api/uploads/init
 *  - PUT    /api/uploads/:uploadId/chunks/:index
 *  - POST   /api/uploads/:uploadId/complete
 *  - (opt)  GET /api/uploads/:uploadId/status
 *
 * Designed to be called from App.js with minimal glue code.
 */

import { appInsights } from "../telemetry/appInsights";
import { apiRequest, getAccessToken } from "../authConfig";
import Logger from "../telemetry/logger";


// Retryable status codes (tune as needed)
const isRetryableStatus = (status) =>
  status === 408 ||
  status === 429 ||
  (status >= 500 && status < 600);

// Sleep with jitter
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function fetchWithTimeout(url, options, timeoutMs) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(url, { ...options, signal: controller.signal });
    return res;
  } finally {
    clearTimeout(timeoutId);
  }
}

async function fetchWithRetry(url, options, retryOpts = {}) {
  const {
    maxAttempts = 5,
    baseDelayMs = 400,
    timeoutMs = 60_000,
  } = retryOpts;

  let attempt = 0;
  let lastErr;

  while (attempt < maxAttempts) {
    attempt++;
    try {
      const res = await fetchWithTimeout(url, options, timeoutMs);

      if (res.ok) return res;

      if (!isRetryableStatus(res.status)) {
        // Non-retryable response
        return res;
      }

      lastErr = new Error(`HTTP ${res.status}`);
    } catch (e) {
      // Network error / AbortError
      lastErr = e;
      // If abort, we still retry (unless caller wants different behavior)
    }

    const delay = Math.random() * baseDelayMs * Math.pow(2, attempt - 1);
    await sleep(delay);
  }

  throw lastErr;
}

async function readJsonSafe(res) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

function buildApiBase(backendEndpoint) {
  // backendEndpoint optional; if not provided assume same origin
  const base = backendEndpoint ? backendEndpoint.replace(/\/+$/, "") : "";
  return base;
}

/**
 * Upload an audio File to backend using chunked upload.
 *
 * @param {Object} params
 * @param {File} params.file - Browser File object
 * @param {Object} params.msalInstance - MSAL instance (from msalContext)
 * @param {Object} params.account - MSAL account object
 * @param {string[]} params.scopes - scopes for token
 * @param {string} [params.backendEndpoint] - base backend URL (optional)
 * @param {Object} [params.metadata] - custom metadata passed to /init
 * @param {(info: {stage: string, uploadedBytes: number, totalBytes: number, percent: number, chunkIndex?: number, totalChunks?: number}) => void} [params.onProgress]
 * @param {number} [params.chunkConcurrency] - 1..N (default 1; safest)
 * @param {number} [params.perChunkTimeoutMs] - per-chunk timeout (default 60s)
 * @param {boolean} [params.resumeIfPossible] - if true, calls /status and skips already uploaded chunks
 *
 * @returns {Promise<{uploadId: string, blobName: string}>}
 */
export async function uploadAudioInChunks({
  file,
  msalInstance,
  account,
  //scopes,
  backendEndpoint,
  metadata = {},
  onProgress,
  chunkConcurrency = 1,
  perChunkTimeoutMs = 60_000,
  resumeIfPossible = true,
}) {
  if (!(file instanceof File)) {
    Logger.error("uploadAudioInChunks: 'file' must be a File.")
    return;
  }

  if (!account){
    Logger.error(`uploadAudioInChunks: ${file.name}: missing MSAL account.`)
    return;
  }

  if (!msalInstance){
    Logger.error(`uploadAudioInChunks: ${file.name}: missing MSAL instance.`)
    return;
  }
  // if (!Array.isArray(scopes) || scopes.length === 0) {
  //    Logger.error("uploadAudioInChunks: missing scopes.")
  //   return;
  // }

  const apiBase = buildApiBase(backendEndpoint);

  // --- helper to call endpoints with bearer token ---
  const authedFetch = async (path, token, options = {}, retryOpts) => {
    const url = `${apiBase}${path}`;
    const headers = {
      ...(options.headers || {}),
      Authorization: `Bearer ${token}`,
    };
    return fetchWithRetry(url, { ...options, headers }, retryOpts);
  };

  // --- 1) init ---
  //let token = await acquireApiToken(msalInstance, account, scopes, false);
  let token = await getAccessToken(msalInstance, account, apiRequest);
  const initBody = {
    fileName: file.name,
    totalBytes: file.size,
    contentType: file.type || "application/octet-stream",
    metadata,
  };

  let initRes = await authedFetch(
    `/api/uploads/init`,
    token,
    {
      method: "POST",
      mode: "cors",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(initBody),
    },
    { timeoutMs: 30_000 }
  );

  // handle 401 once with force refresh
  if (initRes.status === 401) {
    Logger.error(`uploadAudioInChunks: ${file.name}: Received 401, retrying once with fresh token...`);
    token = await getAccessToken(msalInstance, account, apiRequest);
    initRes = await authedFetch(
      `/api/uploads/init`,
      token,
      {
        method: "POST",
        mode: "cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(initBody),
      },
      { timeoutMs: 30_000 }
    );
  }

  if (!initRes.ok) {
    const msg = await initRes.text().catch(() => "");
    Logger.error(`uploadAudioInChunks: ${file.name}: init failed: ${initRes.status} ${msg}`)
    return;
  }

  const initJson = await initRes.json();
  const { uploadId, chunkSize, totalChunks, blobName } = initJson;

  if (!uploadId || !chunkSize) {
    Logger.error(`uploadAudioInChunks: ${file.name}: init response missing uploadId/chunkSize`)
    return;
  }

  // progress state
  let uploadedBytes = 0;
  const totalBytes = file.size;

  // Optionally resume: ask server which chunks it already has
  let alreadyHave = new Set();
  if (resumeIfPossible) {
    try {
      token = await getAccessToken(msalInstance, account, apiRequest);
      const statusRes = await authedFetch(
        `/api/uploads/${encodeURIComponent(uploadId)}/status`,
        token,
        { method: "GET", mode: "cors" },
        { timeoutMs: 15_000, maxAttempts: 2 }
      );

      if (statusRes.ok) {
        const status = await statusRes.json();
        if (Array.isArray(status.received)) {
          alreadyHave = new Set(status.received);
          // estimate bytes already uploaded (approx; last chunk may be smaller)
          let est = 0;
          for (const idx of alreadyHave) {
            const start = idx * chunkSize;
            const end = Math.min(start + chunkSize, totalBytes);
            est += Math.max(0, end - start);
          }
          uploadedBytes = est;
        }
      }
    } catch {
      // ignore status failures; continue as fresh upload
    }
  }

  const report = (stage, extra = {}) => {
    if (!onProgress) return;
    const percent = totalBytes ? Math.round((uploadedBytes / totalBytes) * 100) : 0;
    onProgress({
      stage,
      uploadedBytes,
      totalBytes,
      percent,
      totalChunks: totalChunks ?? Math.ceil(totalBytes / chunkSize),
      ...extra,
    });
  };

  report("init", { uploadId, blobName });

  // --- 2) chunk upload (sequential or limited concurrency) ---
  // Build a list of chunk indices to send
  const computedTotalChunks = totalChunks ?? Math.ceil(totalBytes / chunkSize);
  const pending = [];
  for (let i = 0; i < computedTotalChunks; i++) {
    if (!alreadyHave.has(i)) pending.push(i);
  }

  // Each chunk upload is idempotent via stable Idempotency-Key per chunk
  const uploadChunk = async (index) => {
    const start = index * chunkSize;
    const endExclusive = Math.min(start + chunkSize, totalBytes);
    const chunk = file.slice(start, endExclusive);

    const contentRange = `bytes ${start}-${endExclusive - 1}/${totalBytes}`;
    const chunkKey = `${uploadId}:${index}`;

    report("chunk_start", { chunkIndex: index });

    let res = await authedFetch(
      `/api/uploads/${encodeURIComponent(uploadId)}/chunks/${index}`,
      token,
      {
        method: "PUT",
        mode: "cors",
        headers: {
          "Content-Type": "application/octet-stream",
          "Content-Range": contentRange,
          "Idempotency-Key": chunkKey,
        },
        body: chunk,
      },
      {
        timeoutMs: perChunkTimeoutMs,
        maxAttempts: 5,
        baseDelayMs: 400,
      }
    );

    // If token expired mid-run, refresh and retry once
    if (res.status === 401) {
      token = await getAccessToken(msalInstance, account, apiRequest);
      res = await authedFetch(
        `/api/uploads/${encodeURIComponent(uploadId)}/chunks/${index}`,
        token,
        {
          method: "PUT",
          mode: "cors",
          headers: {
            "Content-Type": "application/octet-stream",
            "Content-Range": contentRange,
            "Idempotency-Key": chunkKey,
          },
          body: chunk,
        },
        {
          timeoutMs: perChunkTimeoutMs,
          maxAttempts: 3,
          baseDelayMs: 500,
        }
      );
    }

    if (!res.ok) {
      const msg = await res.text().catch(() => "");
      Logger.error(`uploadAudioInChunks: ${file.name}: chunk ${index} failed: ${res.status} ${msg}`)
      return;
    }

    // update progress after successful chunk
    uploadedBytes += (endExclusive - start);
    report("chunk_done", { chunkIndex: index });
  };

  // Simple concurrency pool
  async function runPool(items, worker, concurrency) {
    const queue = items.slice();
    const workers = Array.from({ length: Math.max(1, concurrency) }, async () => {
      while (queue.length) {
        const item = queue.shift();
        await worker(item);
      }
    });
    await Promise.all(workers);
  }

  report("uploading", { remainingChunks: pending.length });

  await runPool(pending, uploadChunk, chunkConcurrency);

  // --- 3) complete ---
  report("completing");

  let completeRes = await authedFetch(
    `/api/uploads/${encodeURIComponent(uploadId)}/complete`,
    token,
    {
      method: "POST",
      mode: "cors",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    },
    { timeoutMs: 60_000, maxAttempts: 4 }
  );

  if (completeRes.status === 401) {
    token = await getAccessToken(msalInstance, account, apiRequest);
    completeRes = await authedFetch(
      `/api/uploads/${encodeURIComponent(uploadId)}/complete`,
      token,
      {
        method: "POST",
        mode: "cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      },
      { timeoutMs: 60_000, maxAttempts: 3 }
    );
  }

  if (!completeRes.ok) {
    const msg = await completeRes.text().catch(() => "");
    Logger.error(`uploadAudioInChunks: ${file.name}: complete failed: ${completeRes.status} ${msg}`)
    return;
  }

  // Could be JSON or empty; we don’t require it
  await readJsonSafe(completeRes);

  uploadedBytes = totalBytes;
  report("done");

  return { uploadId, blobName: blobName ?? `${uploadId}` };
}
